import React, { createContext, useContext, useEffect, useState } from 'react';
import { partnersAuthAPI } from '../lib/partners-portal-api';

const PartnersPortalAuthContext = createContext(null);

export const usePartnersPortalAuth = () => {
  const ctx = useContext(PartnersPortalAuthContext);
  if (!ctx) throw new Error('usePartnersPortalAuth must be inside PartnersPortalAuthProvider');
  return ctx;
};

export const PartnersPortalAuthProvider = ({ children }) => {
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const boot = async () => {
      const token = localStorage.getItem('partners_portal_token');
      if (!token) { setLoading(false); return; }
      try {
        const res = await partnersAuthAPI.me();
        setEmployee(res.data);
      } catch {
        localStorage.removeItem('partners_portal_token');
        localStorage.removeItem('partners_portal_employee');
      } finally {
        setLoading(false);
      }
    };
    boot();
  }, []);

  const setSession = (token, emp) => {
    localStorage.setItem('partners_portal_token', token);
    localStorage.setItem('partners_portal_employee', JSON.stringify(emp));
    setEmployee(emp);
  };

  const login = async (email, password) => {
    const res = await partnersAuthAPI.login(email, password);
    setSession(res.data.access_token, res.data.employee);
    try {
      const me = await partnersAuthAPI.me();
      if (me.data?.brand !== 'partners') {
        // Not a partners intern — refuse and clean up
        localStorage.removeItem('partners_portal_token');
        localStorage.removeItem('partners_portal_employee');
        setEmployee(null);
        const err = new Error('This account is not registered as a Brightridge Partners intern.');
        err.response = { data: { detail: 'This account is not registered as a Brightridge Partners intern. Use /portal/login instead.' } };
        throw err;
      }
      setEmployee(me.data);
    } catch (e) { throw e; }
    return res.data.employee;
  };

  const acceptInvite = async (token, password) => {
    const res = await partnersAuthAPI.acceptInvite(token, password);
    setSession(res.data.access_token, res.data.employee);
    try {
      const me = await partnersAuthAPI.me();
      setEmployee(me.data);
    } catch {}
    return res.data.employee;
  };

  const logout = () => {
    localStorage.removeItem('partners_portal_token');
    localStorage.removeItem('partners_portal_employee');
    setEmployee(null);
  };

  const refresh = async () => {
    try {
      const res = await partnersAuthAPI.me();
      setEmployee(res.data);
    } catch {}
  };

  return (
    <PartnersPortalAuthContext.Provider value={{ employee, loading, login, acceptInvite, logout, refresh, isAuthenticated: !!employee }}>
      {children}
    </PartnersPortalAuthContext.Provider>
  );
};
